/**
 * quoteTerms.js — Quote terms compat + resolution layer
 *
 * Quotes historically stored `quotes.terms` as `string[]` (array of enabled
 * term IDs). To support immutable terms (snapshots) + per-quote overrides
 * + custom clauses (PR 3) + an editable settings library (PR 2), we transition
 * to `object[]` snapshots:
 *
 *   [{ id, label, body, source: 'default'|'library'|'custom' }, ...]
 *
 * This module is the single read/write boundary. Every consumer of
 * `quote.terms` (PDF, email, preview, browser) routes through here so both
 * shapes render identically.
 *
 * Old quotes (string[]) → resolved against the effective library (DEFAULT_TERMS
 * overlaid with `settings.data.quoteTerms` overrides).
 * New quotes (object[]) → snapshot bodies used as-is; immutable.
 */

import { DEFAULT_TERMS } from '../data/defaultTerms';

// ── Shape detection ────────────────────────────────────────────────────────

/**
 * True when `terms` is the legacy `string[]` (array of IDs) shape.
 * False for the new `object[]` snapshot shape, null, undefined, or empty.
 */
export function isLegacyTerms(terms) {
  if (!Array.isArray(terms) || terms.length === 0) return false;
  return typeof terms[0] === 'string';
}

// ── Effective library (DEFAULT_TERMS ⊕ settings overrides) ────────────────

/**
 * Build the effective terms library from DEFAULT_TERMS overlaid with the
 * user's settings overrides at `settings.data.quoteTerms`.
 *
 * Override shape (array of):
 *   { id, label?, body?, type?, triggerCategories?, on?, isCustomLibrary? }
 *
 * - Matching id → patches label/body/type/triggerCategories/on on the default
 * - id not in defaults + `isCustomLibrary: true` → adds a new library term
 * - Other ids without isCustomLibrary are ignored (likely stale)
 *
 * Returns Array<{ id, label, body, type, triggerCategories, on, source }>.
 */
export function getEffectiveLibrary(settings) {
  const overrides = Array.isArray(settings?.data?.quoteTerms)
    ? settings.data.quoteTerms
    : [];

  const byId = new Map();

  // Seed with defaults
  for (const t of DEFAULT_TERMS) {
    byId.set(t.id, { ...t, source: 'default' });
  }

  // Overlay overrides
  for (const ov of overrides) {
    if (!ov || !ov.id) continue;
    const existing = byId.get(ov.id);

    if (existing) {
      byId.set(ov.id, {
        ...existing,
        ...(ov.label != null ? { label: ov.label } : {}),
        ...(ov.body != null ? { body: ov.body } : {}),
        ...(ov.note != null ? { note: ov.note } : {}),
        ...(ov.type != null ? { type: ov.type } : {}),
        ...(Array.isArray(ov.triggerCategories) ? { triggerCategories: ov.triggerCategories } : {}),
        ...(typeof ov.on === 'boolean' ? { on: ov.on } : {}),
        isOverridden: true,
      });
    } else if (ov.isCustomLibrary) {
      byId.set(ov.id, {
        id: ov.id,
        label: ov.label || 'Untitled',
        body: ov.body || '',
        note: ov.note || '',
        type: ov.type || 'optional',
        triggerCategories: Array.isArray(ov.triggerCategories) ? ov.triggerCategories : [],
        on: typeof ov.on === 'boolean' ? ov.on : false,
        source: 'library',
        isCustomLibrary: true,
      });
    }
  }

  return Array.from(byId.values());
}

// ── Read: resolve a saved quote's terms into renderable objects ───────────

/**
 * Resolve a saved quote's `terms` field into the array of full term objects
 * that are *enabled* on that quote. Used by PDF, email, preview, browser detail.
 *
 * Handles both shapes:
 *   - Legacy string[] of IDs → looked up in `getEffectiveLibrary(settings)`
 *   - New object[] of snapshots → returned as-is (immutable bodies)
 *
 * Returns Array<{ id, label, body, type?, triggerCategories?, source, on: true }>.
 * Only enabled terms; caller doesn't need to filter.
 */
export function resolveTerms(quote, settings) {
  const terms = quote?.terms;
  if (!Array.isArray(terms) || terms.length === 0) return [];

  if (isLegacyTerms(terms)) {
    const library = getEffectiveLibrary(settings);
    const enabled = new Set(terms);
    return library
      .filter((t) => enabled.has(t.id))
      .map((t) => ({
        id: t.id,
        label: t.label,
        body: t.body,
        type: t.type,
        triggerCategories: t.triggerCategories || [],
        source: t.source || 'default',
        on: true,
      }));
  }

  // New shape: object[] of snapshots. Filter out any stray off entries and
  // missing-id entries to be defensive.
  return terms
    .filter((t) => t && t.id && (t.on === undefined || t.on === true))
    .map((t) => ({
      id: t.id,
      label: t.label || '',
      body: t.body || '',
      type: t.type,
      triggerCategories: Array.isArray(t.triggerCategories) ? t.triggerCategories : [],
      source: t.source || 'default',
      on: true,
    }));
}

// ── Write: snapshot the builder's activeTerms for persistence ─────────────

/**
 * Convert the builder's `activeTerms` (full term objects with computed `on`)
 * into the persistence snapshot shape for writing to `quotes.terms` (JSONB).
 *
 * Only enabled terms are included. Each entry stores label + body so the term
 * is immutable: later edits to DEFAULT_TERMS or to the settings library will
 * NOT retroactively change this saved quote's rendered text.
 *
 * Returns Array<{ id, label, body, source }>.
 */
export function snapshotTerms(activeTerms) {
  if (!Array.isArray(activeTerms)) return [];
  return activeTerms
    .filter((t) => t && t.on === true && t.id)
    .map((t) => ({
      id: t.id,
      label: t.label || '',
      body: t.body || '',
      source: t.source === 'custom' || t.source === 'library' ? t.source : 'default',
    }));
}

// ── Email body composition ────────────────────────────────────────────────

/**
 * Plain-text rendering of a quote's terms for inclusion in email bodies
 * (the `{{quoteTerms}}` merge tag). Reads from `quote.terms` directly when
 * provided, or from the `job.calcSnapshot.quoteTerms` populated by
 * useQuoteBuilder.buildJobData (which now also writes the snapshot shape).
 *
 * Returns a string. Empty string if no terms.
 */
export function renderTermsAsText(termsOrQuote, settings) {
  // Accept either a raw terms array, a quote object, or a job-like object
  // with calcSnapshot.quoteTerms.
  let terms = null;
  if (Array.isArray(termsOrQuote)) {
    terms = termsOrQuote;
  } else if (termsOrQuote && Array.isArray(termsOrQuote.terms)) {
    terms = termsOrQuote.terms;
  } else if (termsOrQuote && Array.isArray(termsOrQuote?.calcSnapshot?.quoteTerms)) {
    terms = termsOrQuote.calcSnapshot.quoteTerms;
  } else if (termsOrQuote && Array.isArray(termsOrQuote?.quoteTerms)) {
    terms = termsOrQuote.quoteTerms;
  }
  if (!terms || terms.length === 0) return '';

  const resolved = resolveTerms({ terms }, settings);
  if (resolved.length === 0) return '';

  const lines = ['TERMS & CONDITIONS', ''];
  for (const t of resolved) {
    if (!t.label && !t.body) continue;
    lines.push(`• ${t.label || 'Term'}`);
    if (t.body) {
      // Wrap body to readable width, indent under bullet
      const wrapped = wrapText(t.body, 76);
      for (const wl of wrapped) lines.push(`  ${wl}`);
    }
    lines.push('');
  }
  // Trim trailing blank line
  while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
  return lines.join('\n');
}

// Simple word-wrap for plain-text bodies. Preserves explicit newlines.
function wrapText(str, width) {
  const out = [];
  const paragraphs = String(str).split(/\n/);
  for (const para of paragraphs) {
    if (!para) {
      out.push('');
      continue;
    }
    const words = para.split(/\s+/);
    let line = '';
    for (const w of words) {
      if (!line) {
        line = w;
      } else if (line.length + 1 + w.length <= width) {
        line += ' ' + w;
      } else {
        out.push(line);
        line = w;
      }
    }
    if (line) out.push(line);
  }
  return out;
}
